import axios from 'axios';
import { getStorageItem, setStorageItem } from './storageManager';
/**
 * Gets zip code from various sources in priority order:
 * 1. window.app_data.zipCode
 * 2. localStorage locationCoords
 * 3. Fresh location request
 * @returns the zip code or an empty string if not available
 */
const getZipCode = async () => {
    var _a;
    // Check window.app_data first
    let zipCode = ((_a = window === null || window === void 0 ? void 0 : window.app_data) === null || _a === void 0 ? void 0 : _a.zipCode) || '';
    if (zipCode)
        return zipCode;
    try {
        // Check localStorage
        const locationCoords = getStorageItem('locationCoords') || {};
        if (locationCoords === null || locationCoords === void 0 ? void 0 : locationCoords.postalCode) {
            zipCode = locationCoords.postalCode;
        }
        else {
            // Request fresh location
            const { requestLocation } = await import('../components/Viewplans/helper');
            const locationObj = await requestLocation();
            zipCode = (locationObj === null || locationObj === void 0 ? void 0 : locationObj.postalCode) || '';
        }
        // Cache in window.app_data if available
        if (zipCode && window.app_data) {
            window.app_data.zipCode = zipCode;
        }
        return zipCode;
    }
    catch (error) {
        console.error('Error getting zip code:', error);
        return '';
    }
};
/**
 * Fetches teams data for a given zip code
 * @param zipCode the zip code to fetch teams for
 * @returns an array of teams or an empty array if there's an error or no teams are available
 */
const fetchTeamsData = async (zipCode) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    if (!zipCode) {
        return [];
    }
    try {
        const site = ((_b = (_a = window === null || window === void 0 ? void 0 : window.app_data) === null || _a === void 0 ? void 0 : _a.site) === null || _b === void 0 ? void 0 : _b.siteInternalName) || '';
        const apiBaseUrl = (_e = (_d = (_c = window === null || window === void 0 ? void 0 : window.app_data) === null || _c === void 0 ? void 0 : _c.appcmsMain) === null || _d === void 0 ? void 0 : _d.apiConfig) === null || _e === void 0 ? void 0 : _e.apiBaseUrl;
        const apiBaseCachedUrl = (_h = (_g = (_f = window === null || window === void 0 ? void 0 : window.app_data) === null || _f === void 0 ? void 0 : _f.appcmsMain) === null || _g === void 0 ? void 0 : _g.apiConfig) === null || _h === void 0 ? void 0 : _h.apiBaseCachedUrl;
        const response = await axios.get(`${apiBaseCachedUrl || apiBaseUrl}/v4/content/personalization/choices`, {
            params: {
                site,
                contentType: 'team',
                zipcode: zipCode
            },
            headers: {
                'x-api-key': (window === null || window === void 0 ? void 0 : window.xApiKey) || ''
            }
        });
        return ((_j = response === null || response === void 0 ? void 0 : response.data) === null || _j === void 0 ? void 0 : _j.teams) || [];
    }
    catch (error) {
        console.error('Error fetching teams data:', error);
        return [];
    }
};
/**
 * Retrieves user preferred team IDs from sessionStorage
 * @returns {Array<string>} Array of team IDs or empty array
 */
const getUserPreferredTeamIds = () => {
    var _a, _b, _c, _d;
    try {
        const authStore = getStorageItem('AuthenticationStore');
        const userTeams = (_d = (_c = (_b = (_a = authStore === null || authStore === void 0 ? void 0 : authStore.user) === null || _a === void 0 ? void 0 : _a.profiles) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.contentPersonalization) === null || _d === void 0 ? void 0 : _d.teams;
        if (Array.isArray(userTeams) && userTeams.length > 0) {
            return userTeams.map(team => team === null || team === void 0 ? void 0 : team.id).filter(id => id != null);
        }
        return [];
    }
    catch (error) {
        console.error('Error parsing user preferred teams from sessionStorage');
        return [];
    }
};
/**
 * Retrieves zipcode-location based team IDs from sessionStorage
 * @returns {Array} Array of team IDs or empty array
 */
const getLocationBasedTeamIds = () => {
    try {
        const teamsData = getStorageItem('teamsData') || [];
        if (Array.isArray(teamsData) && teamsData.length > 0) {
            const teamIds = teamsData.map(team => team === null || team === void 0 ? void 0 : team.id).filter(id => id != null);
            return teamIds;
        }
        return [];
    }
    catch (error) {
        console.error('Error parsing teamsData from sessionStorage');
        return [];
    }
};
/**
 * Retrieves team IDs with priority: user preferred > location-based > empty array
 * @returns {Array<string>} Array of team IDs or empty array
 */
const getPersonalizedTeamIds = () => {
    const userPreferredTeamIds = getUserPreferredTeamIds();
    if ((userPreferredTeamIds === null || userPreferredTeamIds === void 0 ? void 0 : userPreferredTeamIds.length) > 0)
        return userPreferredTeamIds;
    const locationBasedTeamIds = getLocationBasedTeamIds();
    if ((locationBasedTeamIds === null || locationBasedTeamIds === void 0 ? void 0 : locationBasedTeamIds.length) > 0)
        return locationBasedTeamIds;
    return [];
};
/**
 * Checks teams availability based on user's zipcode and caches the result
 * @returns true if teams are available, false otherwise
 */
const checkTeamsAvailability = async () => {
    try {
        const userPreferredTeam = getUserPreferredTeamIds();
        if (Array.isArray(userPreferredTeam) && userPreferredTeam.length > 0)
            return true;
        const zipCode = await getZipCode();
        const teams = await fetchTeamsData(zipCode);
        // Cache teams data
        setStorageItem('teamsData', teams);
        return teams.length > 0;
    }
    catch (error) {
        console.error('Error checking teams availability:', error);
        setStorageItem('teamsData', []);
        return false;
    }
};
export { getZipCode, fetchTeamsData, getLocationBasedTeamIds, getUserPreferredTeamIds, getPersonalizedTeamIds, checkTeamsAvailability, };
